import { useEffect, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { getCompany, listCompanies } from "@/api/companies";
import type { Company } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Command, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDebounce } from "@/hooks/useDebounce";

interface CompanySearchSelectProps {
  slug: string;
  value: number | null;
  selected: Company | null;
  onChange: (company: Company | null) => void;
  /** A denser trigger, for toolbars of small controls. */
  compact?: boolean;
}

/** Uses the same server search as Companies and the task dialogs. */
export function CompanySearchSelect({ slug, value, selected, onChange, compact = false }: CompanySearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query, 250);
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState<{
    slug: string;
    query: string;
    items: Company[];
    state: "loading" | "ok" | "error";
  } | null>(null);
  const [resolved, setResolved] = useState<{ slug: string; company: Company } | null>(null);

  // Saved views can select a company outside the initial display-name cache.
  useEffect(() => {
    if (!slug || value === null || selected?.id === value) return;
    let cancelled = false;
    getCompany(slug, value)
      .then((company) => !cancelled && setResolved({ slug, company }))
      .catch(() => !cancelled && setResolved(null));
    return () => { cancelled = true; };
  }, [slug, value, selected?.id]);

  useEffect(() => {
    if (!open || !slug) return;
    let cancelled = false;
    setResult({ slug, query: debounced, items: [], state: "loading" });
    listCompanies(slug, { q: debounced.trim() || undefined, page_size: 20 })
      .then((response) => {
        if (!cancelled) setResult({ slug, query: debounced, items: response.items, state: "ok" });
      })
      .catch(() => {
        if (!cancelled) setResult({ slug, query: debounced, items: [], state: "error" });
      });
    return () => { cancelled = true; };
  }, [slug, open, debounced, retry]);

  const current = result?.slug === slug && result.query === query ? result : null;
  const label = selected?.id === value ? selected.name
    : resolved?.slug === slug && resolved.company.id === value ? resolved.company.name
    : value === null ? "Any company" : `Company #${value}`;

  function choose(company: Company | null) {
    onChange(company);
    setOpen(false);
    setQuery("");
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-label="Company" aria-expanded={open}
          disabled={!slug} className={compact ? "h-8 w-[150px] justify-between rounded-lg text-xs" : "h-9 w-[200px] justify-between rounded-lg"}>
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search companies…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandItem value="any-company" onSelect={() => choose(null)}>Any company</CommandItem>
            {(!current || current.state === "loading") && (
              <p role="status" className="p-3 text-sm text-muted-foreground">Searching…</p>
            )}
            {current?.state === "error" && (
              <div role="alert" className="p-3 text-sm">
                Could not load companies.
                <Button variant="ghost" size="sm" onClick={() => setRetry((n) => n + 1)}>Retry</Button>
              </div>
            )}
            {current?.state === "ok" && current.items.length === 0 && (
              <p role="status" className="p-3 text-sm text-muted-foreground">No companies found.</p>
            )}
            {current?.state === "ok" && current.items.map((company) => (
              <CommandItem key={company.id} value={String(company.id)} onSelect={() => choose(company)}>
                <span className="flex-1">{company.name}</span>
                {company.id === value && <Check className="ml-2 h-4 w-4" />}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
